/**
 * Page upload submission — `/projects/:projectId/submissions/new`.
 *
 * Flow 9.A (révision : ordre conforme aux contraintes API/DB) :
 *   1. L'utilisateur saisit `insurer_code` + `insurer_name` (formulaire minimal).
 *   2. Au submit du formulaire : POST /v1/submissions → on récupère `submission.id`.
 *   3. Le composant <PdfUploadDropzone /> apparaît, lié à `submission_id`.
 *   4. Upload : initiate → PUT signed URL → complete (orchestré par le dropzone).
 *   5. Quand le file passe en "ready" : redirect vers /submissions/:id (page 9.B).
 *
 * Pourquoi cet ordre (submission AVANT upload) :
 *  - `app.files` exige au moins un parent (CHECK DB + InitiateFileUploadSchema).
 *  - On veut éviter les fichiers orphelins rattachés à un projet seul puis
 *    "promus" plus tard à une submission inexistante.
 *  - C'est aussi la séquence logique côté courtier : on déclare l'assureur
 *    (insurer_code / name) AVANT d'uploader son devis.
 *
 * 9.A NE déclenche PAS l'extraction. La submission reste en `pending` jusqu'au
 * clic explicite "Lancer l'extraction" (page submission, livré en 9.B).
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { InsurerCatalogBoundary } from "../../components/InsurerCatalogBoundary";
import { PdfUploadDropzone, type DropzoneState } from "../../components/projects/PdfUploadDropzone";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { projectSubmissionReturnHref } from "../../lib/preparationNavigation";
import { useInsurerLoop } from "../../lib/queries/deadlines";
import { useInsurerCatalog } from "../../lib/queries/insurers";
import { useProject } from "../../lib/queries/projects";
import { useCreateSubmission, type SubmissionRow } from "../../lib/queries/submissions";

import { RecipientSlotPicker } from "./_parts/RecipientSlotPicker";

export function UploadSubmissionPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const navigate = useNavigate();
  const location = useLocation();

  const project = useProject(projectId);
  const createSubmission = useCreateSubmission();
  const insurerCatalog = useInsurerCatalog();

  // Formulaire pré-upload — insurer_code + insurer_name (auto-suggéré).
  const [insurerCode, setInsurerCode] = useState<string>("");
  const [insurerName, setInsurerName] = useState<string>("");

  // Chunk 9.AO.1 — le créneau assureur auquel cette proposition répond. Vide =
  // dépôt direct, cas légitime : une proposition peut arriver sans sollicitation.
  const [recipientId, setRecipientId] = useState<string>("");
  const insurerLoop = useInsurerLoop(projectId);

  // Submission créée — visible UNE FOIS le formulaire validé. Le dropzone
  // n'est monté qu'après pour passer `submission_id` correctement.
  const [submission, setSubmission] = useState<SubmissionRow | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  // Pour désactiver le formulaire pendant l'upload et bloquer la navigation
  // si l'utilisateur tente de naviguer en plein PUT.
  const [uploadState, setUploadState] = useState<DropzoneState>({ kind: "idle" });
  const isUploading =
    uploadState.kind !== "idle" && uploadState.kind !== "done" && uploadState.kind !== "error";

  // ---------------------------------------------------------------------
  // Chargement du projet
  // ---------------------------------------------------------------------
  if (project.isLoading) {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <div className="h-32 rounded-xl bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
      </div>
    );
  }
  if (project.error || !project.data) {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          {project.error instanceof ApiException
            ? getApiErrorMessage(project.error, lang) || t("errors.generic")
            : t("errors.generic")}
        </div>
      </div>
    );
  }
  const proj = project.data;

  // ---------------------------------------------------------------------
  // Step 1 — création de la submission (via formulaire)
  // ---------------------------------------------------------------------
  const onSelectInsurer = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    setInsurerCode(code);
    // Un créneau appartient à UN assureur : changer d'assureur invalide le choix
    // précédent. Le garder ferait partir un rattachement que l'API refuserait —
    // ou pire, qu'elle accepterait si les codes coïncidaient par hasard.
    setRecipientId("");
    // Auto-remplit le nom suggéré depuis le registre (modifiable par l'utilisateur).
    if (code) {
      const found = insurerCatalog.activeOptions.find((o) => o.value === code);
      if (found && !insurerName) setInsurerName(found.label);
    }
  };

  const onCreateSubmission = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCreateError(null);
    if (!insurerCode || insurerName.trim().length < 2) return;
    if (!proj.id) return;
    try {
      const sub = await createSubmission.mutateAsync({
        project_id: proj.id,
        insurer_code: insurerCode,
        insurer_name: insurerName.trim(),
        ...(recipientId ? { responds_to_recipient_id: recipientId } : {}),
      });
      setSubmission(sub);
    } catch (err) {
      setCreateError(
        err instanceof ApiException
          ? getApiErrorMessage(err, lang) || t("errors.generic")
          : t("errors.generic"),
      );
    }
  };

  // ---------------------------------------------------------------------
  // Step 2 — quand le dropzone signale "done", redirect vers la page submission.
  // ---------------------------------------------------------------------
  const onUploadStateChange = (s: DropzoneState) => {
    setUploadState(s);
    if (s.kind === "done" && submission) {
      // Petit délai pour que l'utilisateur voie le ✓ avant la nav.
      window.setTimeout(() => {
        navigate(`/submissions/${submission.id}`);
      }, 600);
    }
  };

  // ---------------------------------------------------------------------
  // Rendu
  // ---------------------------------------------------------------------
  return (
    <div className="mx-auto w-full max-w-3xl">
      {/* Sticky header */}
      <header className="sticky top-16 z-10 -mx-4 border-b border-slate-200 bg-slate-50/95 px-4 py-4 backdrop-blur-sm lg:top-0 lg:-mx-8 lg:px-8 lg:py-5">
        <nav aria-label="breadcrumb" className="mb-2 text-sm">
          <Link
            to={projectSubmissionReturnHref(proj.id, location.search)}
            className="focus-visible:ring-brand-500 rounded text-slate-600 hover:text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            ← {proj.title}
          </Link>
        </nav>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          {t("submissions.create.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-600">{t("submissions.create.subtitle")}</p>
      </header>

      <div className="space-y-5 py-6 lg:py-8">
        {/* Step 1 — formulaire insurer (toujours visible, désactivé après création) */}
        <section
          className="rounded-xl border border-slate-200 bg-white shadow-sm"
          aria-labelledby="step-1-title"
        >
          <header className="border-b border-slate-100 px-5 py-3.5">
            <h2 id="step-1-title" className="text-base font-semibold text-slate-900">
              {t("submissions.create.step1_title")}
            </h2>
          </header>
          <div className="px-5 py-5">
            <form onSubmit={onCreateSubmission} className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="insurer_code" className="label">
                  {t("submissions.fields.insurer_code")}
                </label>
                <InsurerCatalogBoundary catalog={insurerCatalog}>
                  {(catalog) => (
                    <select
                      id="insurer_code"
                      value={insurerCode}
                      onChange={onSelectInsurer}
                      disabled={!!submission || createSubmission.isPending}
                      required
                      className="input"
                    >
                      <option value="">
                        — {t("submissions.fields.insurer_code_placeholder")} —
                      </option>
                      {catalog.activeOptions.map((ins) => (
                        <option key={ins.value} value={ins.value}>
                          {ins.label}
                        </option>
                      ))}
                    </select>
                  )}
                </InsurerCatalogBoundary>
              </div>
              <div>
                <label htmlFor="insurer_name" className="label">
                  {t("submissions.fields.insurer_name")}
                </label>
                <input
                  id="insurer_name"
                  type="text"
                  value={insurerName}
                  onChange={(e) => setInsurerName(e.target.value)}
                  disabled={!!submission || createSubmission.isPending}
                  minLength={2}
                  maxLength={100}
                  required
                  className="input"
                />
              </div>
              <RecipientSlotPicker
                insurerCode={insurerCode}
                boucle={insurerLoop.data}
                isLoading={insurerLoop.isLoading}
                value={recipientId}
                onChange={setRecipientId}
                disabled={!!submission || createSubmission.isPending}
              />
              {createError && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 sm:col-span-2"
                >
                  {createError}
                </div>
              )}
              {!submission && (
                <div className="flex justify-end sm:col-span-2">
                  <button
                    type="submit"
                    disabled={
                      !insurerCode || insurerName.trim().length < 2 || createSubmission.isPending
                    }
                    className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    {createSubmission.isPending
                      ? t("app.loading")
                      : t("submissions.create.create_button")}
                  </button>
                </div>
              )}
              {submission && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 sm:col-span-2">
                  ✓ {t("submissions.create.created_ok")}
                </div>
              )}
            </form>
          </div>
        </section>

        {/* Step 2 — dropzone (apparait UNE FOIS la submission créée).
            PRESERVED : `<PdfUploadDropzone>` state machine + signed URL flow + onStateChange. */}
        {submission && (
          <section
            className="rounded-xl border border-slate-200 bg-white shadow-sm"
            aria-labelledby="step-2-title"
          >
            <header className="border-b border-slate-100 px-5 py-3.5">
              <h2 id="step-2-title" className="text-base font-semibold text-slate-900">
                {t("submissions.create.step2_title")}
              </h2>
            </header>
            <div className="px-5 py-5">
              <PdfUploadDropzone
                parentIds={{
                  project_id: proj.id,
                  submission_id: submission.id,
                }}
                onStateChange={onUploadStateChange}
                disabled={false}
              />
              {isUploading && (
                <p className="mt-3 text-xs text-slate-500">
                  {t("submissions.create.upload_warning_no_navigate")}
                </p>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
